import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import ErrorBoundary from "../reporting_utils/ErrorBoundary";
import ReportScheduler from "../reporting_utils/ReportScheduler";
import RefreshButton from "../reporting_utils/RefreshButton";
import DateRange from "../reporting_utils/DateRange";
import CurrentDomains from "../reporting_utils/CurrentDomains";
import AppliedFilters from "../reporting_utils/AppliedFilters";
import ReportFilters from "../reporting_utils/ReportFilters";
import TextDetailsTable from "./TextDetailsTable";
import useFiltersState from "../reporting_utils/useFiltersState";
import moment from "moment";
import { Common } from "../../utils/common";
import { getFilterValues } from "../reporting_utils/misc";
import EmptyReport from "../reporting_utils/EmptyReport";

const pageLoadingFrame = window.pageLoadingFrame;

// Used as initial values for the filter state and as a reference
// for what to set the values to when the filters are cleared
const defaultFilterState = {
  medium_type: [],
  tag_ids: [],
};

function TextDetails({ defaultFiltersFromURL, controllerName, domainId, selectInputs }) {
  const [tableData, setTableData] = useState(null);
  const [filters, setFilters] = useFiltersState(
    defaultFilterState,
    defaultFiltersFromURL,
    domainId
  );
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false);
  const [controlledDateRange, setControlledDateRange] = useState({
    start: moment(filters.start_date, "YYYYMMDD"),
    end: moment(filters.end_date, "YYYYMMDD"),
  });

  // Wrapped in useCallback so these functions only get recreated when the filters
  // state changes. Otherwise they will be recreated every render, which will trigger
  // the useEffect to run every render, causing an infinite loop of rerenders.
  const fetchTableData = useCallback(async () => {
    if (!filters.domains) {
      return;
    }
    pageLoadingFrame("show");
    try {
      const res = await fetch("/text_details", {
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content,
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(getFilterValues(filters)),
      });
      const data = await res.json();
      setTableData(data);
    } catch (error) {
      console.error(error);
      Common.notify("Failed to fetch report data", "error");
    }
    pageLoadingFrame("hide");
  }, [filters]);

  useEffect(() => {
    // Fetches table data whenever the filters change
    // This effect only runs when this function changes, which only happens
    // when the filters change as specified in useCallback's dependency array.
    fetchTableData();
  }, [fetchTableData]);

  return (
    <div className="report-details-container">
      <div className="report-details-header">
        <div className="report-details-title">
          <h1>Text Details</h1>
          <p>A detailed report of all texts for your account</p>
        </div>
        <div className="panel-header">
          <ErrorBoundary>
            {!Common.in_crm_mode() && (
              <ReportScheduler controllerName={controllerName} filters={filters} />
            )}
            <RefreshButton dataCallbacks={fetchTableData} />
            {!sidebarIsOpen && (
              <DateRange
                controlledDateRange={controlledDateRange}
                setControlledDateRange={setControlledDateRange}
                filters={filters}
                setFilters={setFilters}
              />
            )}
          </ErrorBoundary>
        </div>
      </div>
      <ReportFilters
        filters={filters}
        defaultFilterState={defaultFilterState}
        open={sidebarIsOpen}
        setOpen={setSidebarIsOpen}
        setFilters={setFilters}
        controlledDateRange={controlledDateRange}
        setControlledDateRange={setControlledDateRange}
        selectInputs={selectInputs}
      />
      {filters.domains ? (
        <ErrorBoundary>
          <div className="applied-filters-container">
            <div style={{ marginBottom: "20px" }}>
              <button className="btn btn-primary" onClick={() => setSidebarIsOpen(!sidebarIsOpen)}>
                <span className="fa fa-plus" /> Add Filters
              </button>
            </div>
            {!Common.in_crm_mode() && <CurrentDomains filters={filters} setFilters={setFilters} />}
            <ErrorBoundary>
              <AppliedFilters
                defaultFilterState={defaultFilterState}
                filters={filters}
                setFilters={setFilters}
                selectInputs={selectInputs}
              />
            </ErrorBoundary>
          </div>
          <TextDetailsTable tableData={tableData} />
        </ErrorBoundary>
      ) : (
        <EmptyReport onClick={() => setSidebarIsOpen(!sidebarIsOpen)} />
      )}
    </div>
  );
}

TextDetails.propTypes = {
  defaultFiltersFromURL: PropTypes.object.isRequired,
  controllerName: PropTypes.string.isRequired,
  domainId: PropTypes.string,
  selectInputs: PropTypes.object.isRequired,
};

export default TextDetails;
